import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { ReactNode } from "react";
import Login from "./views/login";
import Signup from "./views/signup";
import Confirmation from "./views/confirmation";
import ProfileSetup from "./views/profilesetup";
import ForgotPassword from "./views/forgotPassword";

import RulesPage from "./views/rules/RulesPage";
import ContractAnalysis from "./views/dashboard/contract-analysis/ContractAnalysis";
import { CardComparisonPage } from "./views/dashboard/contract-analysis/CardComparisonPage";
import { PATH_AUTH, PATH_ROOT } from "./routes/paths";
import AuthGuard from "./guards/AuthGuard";
import GuestGuard from "./guards/GuestGuard";
import OrgGuard from "./guards/OrgGuard";
import Onboarding from "./views/onboarding";
import Profile from "./views/dashboard/Profile";
import { ApiTestPage } from "./api/tests/ApiTestPage";
import VaultPage from "./views/vault/VaultPage";
import { ContractIntelligencePage } from "./views/contract-intelligence";
import DashboardPage from "./views/dashboard/DashboardPage";
import DealPipelinePage from "./views/deal-pipeline/DealPipelinePage";
import PeoplePage from "./views/people/PeoplePage";
import InboxPage from "./views/inbox/InboxPage";
import CalendarPage from "./views/calendar/CalendarPage";
import DeliveryManagerPage from "./views/delivery-manager/DeliveryManagerPage";
import IntegrationsPage from "./views/integrations/IntegrationsPage";
import PortfolioPage from "./views/portfolio/PortfolioPage";
import AriDemoRosterPage, { AriDemoClientHubPage } from "./views/portfolio/demo-ari";
import ComposerPage from "./views/composer/ComposerPage";
import ChatPage from "./views/chat/ChatPage";
import CreatePage from "./views/create/CreatePage";
import DealPage from "./views/deals/DealPage";
import { NewDealPage } from "./views/deals/NewDealPage";
import AriDemoInboxPage from "./views/inbox/demo-ari/AriDemoInboxPage";
import { AriDemoWelcomePage } from "./views/demo-ari/welcome";
import { AriDemoDealComparisonPage } from "./views/demo-ari/deal-comparison";
import { FeatureGate } from "./components/FeatureGate";
import { ComingSoon } from "./components/ComingSoon";
import { FeatureFlags } from "./hooks/common/useFeatureFlag";
import InteractiveDemoPage from "./views/interactive-demo/InteractiveDemoPage";
import DemoWrapper from "./components/DemoWrapper";
import { ContractAnalysisDemoRouter } from "./views/dashboard/contract-analysis/ContractAnalysisDemoRouter";
import { useDemoContractAnalysis } from "./hooks/contracts/useDemoContractAnalysis";
import OrganizationsList from "./views/organization/OrganizationsList";
import OrganizationManagement from "./views/organization/OrganizationManagement";
import OrganizationSettings from "./views/organization/OrganizationSettings";
import OrganizationMembers from "./views/organization/OrganizationMembers";
import OrganizationInvitations from "./views/organization/OrganizationInvitations";
import OrganizationRoles from "./views/organization/OrganizationRoles";
import OrganizationIntegrations from "./views/organization/OrganizationIntegrations";
import SettingsPage from "./views/settings/SettingsPage";
import MembersSection from "./views/settings/sections/MembersSection";
import { SettingsPlaceholder } from "./views/settings/sections/SettingsPlaceholder";
import { AuthLayout, AppLayoutWithSidebar } from "./views/app-layout";
import { FlowProviderSetup } from "@/features/guided-flow/components/FlowProviderSetup";
import { RamVizPage } from "./views/RamVizPage";
import "./css/globals.css";

const ProtectedRoute = ({ children }: { children: ReactNode }) => (
  <AuthGuard><OrgGuard>{children}</OrgGuard></AuthGuard>
);

const ProtectedAppLayout = () => (
  <ProtectedRoute>
    <AppLayoutWithSidebar />
  </ProtectedRoute>
);

function DemoRecordingRoute() {
  const { getDemoContractById, demoContractIds } = useDemoContractAnalysis();
  const contract = getDemoContractById(demoContractIds.ARTIST_RECORDING_DEAL);
  if (!contract) return null;
  return <ContractAnalysis providedContract={contract} isDemo={true} />;
}

function DemoPortfolioClientRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  return <Navigate to={PATH_ROOT.demo.rosterClient(clientId ?? "")} replace />;
}

const App = () => {
  return (
    <BrowserRouter>
      <FlowProviderSetup />
      <Toaster
        position="top-right"
        toastOptions={{
          classNames: {
            toast: "!bg-neutral-800 !text-white !border !border-white/10 !rounded-2xl !p-4 !backdrop-blur-sm",
            title: '!font-medium',
            description: '!font-normal !text-neutral-400',
          }
        }}
      />
      <Routes>
          <Route path="/" element={<Navigate to={PATH_ROOT.demo.welcome} replace />} />

          {/* RAM — quick viz test, no auth */}
          <Route path="/ram-vis" element={<RamVizPage />} />

          {/* Ari demo welcome — full-screen, no sidebar, no auth */}
          <Route
            path={PATH_ROOT.demo.welcome}
            element={
              <DemoWrapper>
                <AriDemoWelcomePage />
              </DemoWrapper>
            }
          />
          <Route path="/demo/welcome" element={<Navigate to={PATH_ROOT.demo.welcome} replace />} />
          <Route element={<AuthLayout />}>
            <Route
              path={PATH_AUTH.signIn}
              element={
                <GuestGuard>
                  <Login />
                </GuestGuard>
              }
            />
            <Route path={PATH_AUTH.root} element={<Navigate to={PATH_AUTH.signIn} replace />} />
            <Route path="/auth/login" element={<Navigate to={PATH_AUTH.signIn} replace />} />
            <Route
              path={PATH_AUTH.signup}
              element={
                <GuestGuard>
                  <Signup />
                </GuestGuard>
              }
            />
            <Route
              path={PATH_AUTH.confirmation}
              element={
                <GuestGuard>
                  <Confirmation />
                </GuestGuard>
              }
            />
            <Route
              path={PATH_AUTH.profileSetup}
              element={
                <GuestGuard>
                  <ProfileSetup />
                </GuestGuard>
              }
            />
            <Route
              path={PATH_AUTH.forgotPassword}
              element={
                <GuestGuard>
                  <ForgotPassword />
                </GuestGuard>
              }
            />

            {/* Tier 2: Auth splash routes - no sidebar, auth required */}
            <Route
              path={PATH_ROOT.organization.organizations}
              element={
                <AuthGuard>
                  <OrganizationsList />
                </AuthGuard>
              }
            />
            <Route
              path={PATH_AUTH.onboarding}
              element={
                <AuthGuard>
                  <Onboarding />
                </AuthGuard>
              }
            />
          </Route>

          {/* Tier 3: Main app routes - with sidebar, auth + org required */}
          <Route element={<ProtectedAppLayout />}>
            <Route path={PATH_ROOT.apiTest} element={<ApiTestPage />} />
            <Route
              path={PATH_ROOT.main.manager}
              element={<FeatureGate flag={FeatureFlags.PROD_DASHBOARD} fallback={<ComingSoon featureName="Dashboard" />}><DashboardPage /></FeatureGate>}
            />
            <Route path={PATH_ROOT.main.vault} element={<VaultPage />} />
            <Route path={`${PATH_ROOT.main.analysis}/:contractId`} element={<ContractAnalysis />} />
            <Route
              path={PATH_ROOT.main.dashboard}
              element={<FeatureGate flag={FeatureFlags.PROD_DASHBOARD} fallback={<ComingSoon featureName="Dashboard" />}><DashboardPage /></FeatureGate>}
            />
            <Route path={PATH_ROOT.main.profile} element={<Profile />} />
            <Route path={PATH_ROOT.main.comparison} element={<CardComparisonPage />} />
            <Route
              path={PATH_ROOT.main.contractIq}
              element={
                <FeatureGate flag={FeatureFlags.PROD_CONTRACT_IQ} fallback={<ComingSoon featureName="Contract IQ" />}>
                  <ContractIntelligencePage />
                </FeatureGate>
              }
            />

            {/* Organization management routes (require org selected) */}
            <Route
              path={PATH_ROOT.organization.dashboard}
              element={<FeatureGate flag={FeatureFlags.PROD_DASHBOARD} fallback={<ComingSoon featureName="Dashboard" />}><DashboardPage /></FeatureGate>}
            />
            <Route path={PATH_ROOT.organization.management} element={<OrganizationManagement />} />
            <Route path={PATH_ROOT.organization.settings} element={<OrganizationSettings />} />
            <Route path={PATH_ROOT.organization.members} element={<OrganizationMembers />} />
            <Route path={PATH_ROOT.organization.invitations} element={<OrganizationInvitations />} />
            <Route path={PATH_ROOT.organization.roles} element={<OrganizationRoles />} />
            <Route path={PATH_ROOT.organization.integrations} element={<OrganizationIntegrations />} />

            <Route path={PATH_ROOT.main.create} element={<FeatureGate flag={FeatureFlags.PROD_CONVERSATIONS} fallback={<ComingSoon featureName="Ask Ari" />}><CreatePage /></FeatureGate>} />
            <Route path={PATH_ROOT.main.chat} element={<FeatureGate flag={FeatureFlags.PROD_CONVERSATIONS} fallback={<ComingSoon featureName="Ask Ari" />}><ChatPage /></FeatureGate>} />
            <Route
              path={PATH_ROOT.main.dealPipeline}
              element={
                <FeatureGate flag={FeatureFlags.PROD_DEAL_PIPELINE} fallback={<ComingSoon featureName="Deal Pipeline" />}>
                  <DealPipelinePage />
                </FeatureGate>
              }
            />
            <Route path={`${PATH_ROOT.main.deal}/:id`} element={<DealPage />} />
            <Route path={PATH_ROOT.main.newDeal} element={<NewDealPage />} />
            <Route
              path={PATH_ROOT.main.people}
              element={
                <FeatureGate flag={FeatureFlags.PROD_PEOPLE} fallback={<ComingSoon featureName="People" />}>
                  <PeoplePage />
                </FeatureGate>
              }
            />
            <Route
              path={PATH_ROOT.main.inbox}
              element={
                <FeatureGate flag={FeatureFlags.PROD_INBOX} fallback={<ComingSoon featureName="Inbox" />}>
                  <InboxPage />
                </FeatureGate>
              }
            />
            <Route
              path={PATH_ROOT.main.calendar}
              element={
                <FeatureGate flag={FeatureFlags.PROD_CALENDAR} fallback={<ComingSoon featureName="Calendar" />}>
                  <CalendarPage />
                </FeatureGate>
              }
            />
            <Route
              path={PATH_ROOT.main.deliveryManager}
              element={
                <FeatureGate flag={FeatureFlags.PROD_DELIVERY_MANAGER} fallback={<ComingSoon featureName="Delivery Manager" />}>
                  <DeliveryManagerPage />
                </FeatureGate>
              }
            />
            <Route path={PATH_ROOT.main.integrations} element={<Navigate to={PATH_ROOT.settings.integrations} replace />} />

            {/* Settings — grouped account & organization settings */}
            <Route path={PATH_ROOT.settings.root} element={<SettingsPage />}>
              <Route index element={<Navigate to="members" replace />} />
              <Route path="profile" element={<SettingsPlaceholder section="profile" />} />
              <Route path="sessions" element={<SettingsPlaceholder section="sessions" />} />
              <Route path="notifications" element={<SettingsPlaceholder section="notifications" />} />
              <Route path="general" element={<SettingsPlaceholder section="general" />} />
              <Route path="members" element={<MembersSection />} />
              <Route
                path="integrations"
                element={
                  <FeatureGate flag={FeatureFlags.PROD_INTEGRATIONS} fallback={<ComingSoon featureName="Integrations" />}>
                    <IntegrationsPage />
                  </FeatureGate>
                }
              />
              <Route path="plan" element={<SettingsPlaceholder section="plan" />} />
            </Route>
            <Route
              path={PATH_ROOT.main.portfolio}
              element={
                <FeatureGate flag={FeatureFlags.PROD_PORTFOLIO} fallback={<ComingSoon featureName="Portfolio" />}>
                  <PortfolioPage />
                </FeatureGate>
              }
            />
            <Route
              path={PATH_ROOT.main.composer}
              element={
                <FeatureGate flag={FeatureFlags.PROD_COMPOSER} fallback={<ComingSoon featureName="Composer" />}>
                  <ComposerPage />
                </FeatureGate>
              }
            />
          </Route>

          <Route element={<AppLayoutWithSidebar />}>

            {/* Interactive demo */}
            <Route
              path="/interactive-demo"
              element={<InteractiveDemoPage />}
            />

            {/* Demo routes — same production views, gated by DEMO_* flags (no OrgGuard) */}
            <Route path={PATH_ROOT.demo.rules} element={<DemoWrapper><RulesPage /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.interactive} element={<InteractiveDemoPage />} />
            <Route path={PATH_ROOT.demo.selector} element={<DemoWrapper><OrganizationsList isDemo={true} variant="selector" /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.dashboard} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_DASHBOARD} fallback={<ComingSoon featureName="Dashboard" />}><DashboardPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.vault} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_VAULT} fallback={<ComingSoon featureName="Contract Vault" />}><VaultPage /></FeatureGate></DemoWrapper>} />
            <Route path={`${PATH_ROOT.demo.analysis}/:contractId`} element={<DemoWrapper><ContractAnalysisDemoRouter /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.recording} element={<DemoWrapper><DemoRecordingRoute /></DemoWrapper>} />
            <Route path={`${PATH_ROOT.demo.deal}/:id`} element={<DemoWrapper><DealPage /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.comparison} element={<DemoWrapper><CardComparisonPage isDemo={true} /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.dealComparison} element={<DemoWrapper><AriDemoDealComparisonPage /></DemoWrapper>} />
            <Route
              path={PATH_ROOT.demo.contractIq}
              element={
                <DemoWrapper>
                  <FeatureGate flag={FeatureFlags.DEMO_CONTRACT_IQ} fallback={<ComingSoon featureName="Contract IQ" />}>
                    <ContractIntelligencePage />
                  </FeatureGate>
                </DemoWrapper>
              }
            />
            <Route path={PATH_ROOT.demo.dealPipeline} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_DEAL_PIPELINE} fallback={<ComingSoon featureName="Deal Pipeline" />}><DealPipelinePage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.newDeal} element={<DemoWrapper><NewDealPage /></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.create} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_CONVERSATIONS} fallback={<ComingSoon featureName="Ask Ari" />}><CreatePage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.chat} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_CONVERSATIONS} fallback={<ComingSoon featureName="Ask Ari" />}><ChatPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.people} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_PEOPLE} fallback={<ComingSoon featureName="People" />}><PeoplePage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.inbox} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_INBOX} fallback={<ComingSoon featureName="Inbox" />}><AriDemoInboxPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.calendar} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_CALENDAR} fallback={<ComingSoon featureName="Calendar" />}><CalendarPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.deliveryManager} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_DELIVERY_MANAGER} fallback={<ComingSoon featureName="Delivery Manager" />}><DeliveryManagerPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.integrations} element={<Navigate to={PATH_ROOT.demo.settingsIntegrations} replace />} />

            {/* Demo settings — grouped account & organization settings */}
            <Route path={PATH_ROOT.demo.settings} element={<DemoWrapper><SettingsPage /></DemoWrapper>}>
              <Route index element={<Navigate to="members" replace />} />
              <Route path="profile" element={<SettingsPlaceholder section="profile" />} />
              <Route path="sessions" element={<SettingsPlaceholder section="sessions" />} />
              <Route path="notifications" element={<SettingsPlaceholder section="notifications" />} />
              <Route path="general" element={<SettingsPlaceholder section="general" />} />
              <Route path="members" element={<MembersSection />} />
              <Route path="integrations" element={<FeatureGate flag={FeatureFlags.DEMO_INTEGRATIONS} fallback={<ComingSoon featureName="Integrations" />}><IntegrationsPage /></FeatureGate>} />
              <Route path="plan" element={<SettingsPlaceholder section="plan" />} />
            </Route>
            <Route path={PATH_ROOT.demo.roster} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_PORTFOLIO} fallback={<ComingSoon featureName="Roster" />}><AriDemoRosterPage /></FeatureGate></DemoWrapper>} />
            <Route path={`${PATH_ROOT.demo.roster}/:clientId`} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_PORTFOLIO} fallback={<ComingSoon featureName="Roster" />}><AriDemoClientHubPage /></FeatureGate></DemoWrapper>} />
            <Route path="/demo/portfolio" element={<Navigate to={PATH_ROOT.demo.roster} replace />} />
            <Route path="/demo/portfolio/:clientId" element={<DemoPortfolioClientRedirect />} />
            <Route path={PATH_ROOT.demo.composer} element={<DemoWrapper><FeatureGate flag={FeatureFlags.DEMO_COMPOSER} fallback={<ComingSoon featureName="Composer" />}><ComposerPage /></FeatureGate></DemoWrapper>} />
            <Route path={PATH_ROOT.demo.profile} element={<DemoWrapper><Profile isDemo={true} /></DemoWrapper>} />

            {/* Demo Organization routes */}
            <Route path={PATH_ROOT.demo.organizations} element={<DemoWrapper><OrganizationsList isDemo={true} variant="management" /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/manage" element={<DemoWrapper><OrganizationManagement isDemo={true} /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/settings" element={<DemoWrapper><OrganizationSettings isDemo={true} /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/members" element={<DemoWrapper><OrganizationMembers isDemo={true} /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/invitations" element={<DemoWrapper><OrganizationInvitations isDemo={true} /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/roles" element={<DemoWrapper><OrganizationRoles isDemo={true} /></DemoWrapper>} />
            <Route path="/demo/org/:orgId/integrations" element={<DemoWrapper><OrganizationIntegrations isDemo={true} /></DemoWrapper>} />
          </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
